import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { internshipService, analyticsService, authService } from '../services/api';
import { Briefcase, Mail, MessageSquare, Trophy } from 'lucide-react';

function DashboardPage() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const [stats, setStats] = useState(null);
    const [recentApps, setRecentApps] = useState([]);
    const [loading, setLoading] = useState(true);
    const [resendSent, setResendSent] = useState(false);

    const displayName = user?.profile?.full_name || user?.email?.split('@')[0];

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [analyticsRes, appsRes] = await Promise.all([
                    analyticsService.getDashboard(),
                    internshipService.list(),
                ]);
                setStats(analyticsRes.data);
                setRecentApps(appsRes.data.slice(0, 5));
            } catch (err) {
                console.error('Dashboard load failed');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const resendVerification = async () => {
        try {
            await authService.resendVerification();
            setResendSent(true);
        } catch (err) {
            alert(t('common.error'));
        }
    };

    const statusLabel = (status) => {
        const key = `internships.${status}`;
        return t(key);
    };

    const STATUS_COLORS = {
        applied: '#3b82f6',
        interview: '#f59e0b',
        offer: '#10b981',
        rejected: '#ef4444',
        withdrawn: '#6b7280',
    };

    const greeting = () => {
        const hour = new Date().getHours();
        if (hour < 12) return t('dashboard.greeting_morning');
        if (hour < 17) return t('dashboard.greeting_afternoon');
        return t('dashboard.greeting_evening');
    };
    if (loading) return (
        <Layout>
            <div className="page-container">
                <div style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>
                    <div style={{ height: '32px', width: '220px', background: '#e2e8f0', borderRadius: '8px', marginBottom: '0.5rem' }} />
                    <div style={{ height: '18px', width: '320px', background: '#e2e8f0', borderRadius: '6px', marginBottom: '2rem' }} />
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
                        {[1,2,3,4].map(i => (
                            <div key={i} style={{ height: '100px', background: '#e2e8f0', borderRadius: '12px' }} />
                        ))}
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        <div style={{ height: '250px', background: '#e2e8f0', borderRadius: '12px' }} />
                        <div style={{ height: '250px', background: '#e2e8f0', borderRadius: '12px' }} />
                    </div>
                </div>
                <style>{`
                    @keyframes pulse {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.4; }
                    }
                `}</style>
            </div>
        </Layout>
    );
    
    return (
        <Layout>
            {/* Email verification banner */}
            {user && !user.is_email_verified && (
                <div style={{
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    padding: '0.75rem 1.5rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    fontSize: '0.9rem',
                    color: '#92400e',
                    gap: '1rem',
                }}>
                    <span>
                        {t('dashboard.verify_email')} <strong>{t('dashboard.free_credits')}</strong>.{' '}
                        {t('dashboard.check_inbox')}
                    </span>
                    {resendSent ? (
                        <span style={{ color: '#15803d', fontWeight: '600' }}>
                            {t('dashboard.email_sent')}
                        </span>
                    ) : (
                        <button
                            onClick={resendVerification}
                            style={{
                                background: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                padding: '0.4rem 1rem',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '0.85rem',
                                fontWeight: '600',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {t('dashboard.resend_email')}
                        </button>
                    )}
                </div>
            )}

            <div className="page-container">

                {/* Header */}
                <div className="dashboard-header">
                    <div>
                        <h1>{greeting()}, {displayName}</h1>
                        <p className="subtitle">{t('dashboard.subtitle')}</p>
                    </div>
                    <Link to="/internships" className="btn-primary" style={{ textDecoration: 'none' }}>
                        {t('dashboard.add_application')}
                    </Link>
                </div>

                {/* Stats */}
                {stats && (
                    <div className="dashboard-stats">
                        <div className="dash-stat-card">
                            <div className="dash-stat-icon" style={{ background: '#dbeafe' }}>
                                <Briefcase size={20} color="#3b82f6" />
                            </div>
                            <div>
                                <h3>{stats.total_applications}</h3>
                                <p>{t('dashboard.total_applications')}</p>
                            </div>
                        </div>
                        <div className="dash-stat-card">
                            <div className="dash-stat-icon" style={{ background: '#dcfce7' }}>
                                <Mail size={20} color="#10b981" />
                            </div>
                            <div>
                                <h3>{stats.response_rate}%</h3>
                                <p>{t('dashboard.response_rate')}</p>
                            </div>
                        </div>
                        <div className="dash-stat-card">
                            <div className="dash-stat-icon" style={{ background: '#fef3c7' }}>
                                <MessageSquare size={20} color="#f59e0b" />
                            </div>
                            <div>
                                <h3>{stats.interview_conversion_rate}%</h3>
                                <p>{t('dashboard.interview_rate')}</p>
                            </div>
                        </div>
                        <div className="dash-stat-card">
                            <div className="dash-stat-icon" style={{ background: '#f3e8ff' }}>
                                <Trophy size={20} color="#8b5cf6" />
                            </div>
                            <div>
                                <h3>{stats.offer_rate}%</h3>
                                <p>{t('dashboard.offer_rate')}</p>
                            </div>
                        </div>
                    </div>
                )}

                <div className="dashboard-grid">

                    {/* Recent Applications */}
                    <div className="card">
                        <div className="card-header">
                            <div>
                                <h2>{t('dashboard.recent_applications')}</h2>
                                <p>{t('dashboard.recent_subtitle')}</p>
                            </div>
                            <Link to="/internships" style={{ fontSize: '0.85rem', color: '#3b82f6' }}>
                                {t('dashboard.view_all')}
                            </Link>
                        </div>
                        {recentApps.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                                <p style={{ marginTop: '0.5rem' }}>{t('dashboard.no_applications')}</p>
                                <Link to="/internships" className="btn-primary"
                                    style={{ display: 'inline-block', marginTop: '1rem', textDecoration: 'none' }}>
                                    {t('dashboard.add_first')}
                                </Link>
                            </div>
                        ) : (
                            <div className="recent-apps-list">
                                {recentApps.map(app => (
                                    <div key={app.id} className="recent-app-item">
                                        <div className="recent-app-company">
                                            <div className="company-initial">
                                                {app.company_name.charAt(0).toUpperCase()}
                                            </div>
                                            <div>
                                                <strong>{app.position}</strong>
                                                <small>{app.company_name}</small>
                                            </div>
                                        </div>
                                        <span
                                            className="status-pill"
                                            style={{
                                                background: STATUS_COLORS[app.status] + '20',
                                                color: STATUS_COLORS[app.status]
                                            }}
                                        >
                                            {statusLabel(app.status)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Quick Actions */}
                    <div className="card">
                        <div className="card-header">
                            <div>
                                <h2>{t('dashboard.quick_actions')}</h2>
                                <p>{t('dashboard.quick_subtitle')}</p>
                            </div>
                        </div>
                        <div className="quick-action-grid">
                            <Link to="/resumes" className="quick-action-item">
                                <div className="quick-action-icon" style={{ background: '#dbeafe' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#3b82f6' }}>CV</span>
                                </div>
                                <strong>{t('dashboard.analyse_resume')}</strong>
                                <small>{t('dashboard.analyse_subtitle')}</small>
                            </Link>
                            <Link to="/interview" className="quick-action-item">
                                <div className="quick-action-icon" style={{ background: '#dcfce7' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#10b981' }}>INT</span>
                                </div>
                                <strong>{t('dashboard.practice_interview')}</strong>
                                <small>{t('dashboard.practice_subtitle')}</small>
                            </Link>
                            <Link to="/career" className="quick-action-item">
                                <div className="quick-action-icon" style={{ background: '#fef3c7' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#f59e0b' }}>AI</span>
                                </div>
                                <strong>{t('dashboard.career_coach')}</strong>
                                <small>{t('dashboard.career_subtitle')}</small>
                            </Link>
                            <Link to="/analytics" className="quick-action-item">
                                <div className="quick-action-icon" style={{ background: '#f3e8ff' }}>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '700', color: '#8b5cf6' }}>AN</span>
                                </div>
                                <strong>{t('dashboard.analytics')}</strong>
                                <small>{t('dashboard.analytics_subtitle')}</small>
                            </Link>
                        </div>
                    </div>

                    {/* Application Pipeline */}
                    {stats && stats.total_applications > 0 && (
                        <div className="card">
                            <div className="card-header">
                                <div>
                                    <h2>{t('dashboard.pipeline')}</h2>
                                    <p>{t('dashboard.pipeline_subtitle')}</p>
                                </div>
                            </div>
                            <div className="pipeline-list">
                                {Object.entries(stats.status_breakdown).map(([status, count]) => (
                                    <div key={status} className="pipeline-item">
                                        <div className="pipeline-label">
                                            <span
                                                className="pipeline-dot"
                                                style={{ background: STATUS_COLORS[status] }}
                                            />
                                                <span>{statusLabel(status)}</span>
                                        </div>
                                        <div className="pipeline-bar-track">
                                            <div
                                                className="pipeline-bar-fill"
                                                style={{
                                                    width: stats.total_applications > 0
                                                        ? `${(count / stats.total_applications) * 100}%`
                                                        : '0%',
                                                    background: STATUS_COLORS[status],
                                                }}
                                            />
                                        </div>
                                        <span className="pipeline-count">{count}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Resume Performance */}
                    {stats?.resume_stats && (
                        <div className="card">
                            <div className="card-header">
                                <div>
                                    <h2>{t('dashboard.resume_performance')}</h2>
                                    <p>{t('dashboard.resume_subtitle')}</p>
                                </div>
                                <Link to="/resumes" style={{ fontSize: '0.85rem', color: '#3b82f6' }}>
                                    {t('resume.analyse_btn')} →
                                </Link>
                            </div>
                            <div className="resume-score-list">
                                {[
                                    { label: t('dashboard.ats_score'), value: stats.resume_stats.avg_ats_score },
                                    { label: t('dashboard.keywords'), value: stats.resume_stats.avg_keyword_score },
                                    { label: t('dashboard.readability'), value: stats.resume_stats.avg_readability_score },
                                ].map(item => (
                                    <div key={item.label} className="resume-score-row">
                                        <span>{item.label}</span>
                                        <div className="resume-score-bar-track">
                                            <div
                                                className="resume-score-bar-fill"
                                                style={{
                                                    width: `${item.value}%`,
                                                    background: item.value >= 70 ? '#10b981'
                                                        : item.value >= 50 ? '#f59e0b' : '#ef4444',
                                                }}
                                            />
                                        </div>
                                        <strong style={{
                                            color: item.value >= 70 ? '#10b981'
                                                : item.value >= 50 ? '#f59e0b' : '#ef4444'
                                        }}>
                                            {item.value}%
                                        </strong>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </Layout>
    );
}

export default DashboardPage;